/**
 * employeeScheduleShared.ts
 * Shared, pure helpers for the Employee Schedule views (UX overhaul):
 * date math, tier grouping, and bar colour tokens. No hex literals — colours
 * come from theme.schedule.*.
 */

import type { Theme } from '@mui/material';
import type { EmployeeBar, EmployeeBarKind, EmployeeBarUser } from '../../services/scheduleEmployeeBars';

// ── date helpers (UTC, Saturday-anchored BST week) ──────────────────────────
export function currentSaturday(): string {
  const now = new Date();
  const dow = now.getUTCDay(); // 0=Sun..6=Sat
  const delta = dow === 6 ? 0 : -(dow + 1);
  const sat = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + delta));
  return sat.toISOString().slice(0, 10);
}

/**
 * Add `n` calendar days to an ISO 'YYYY-MM-DD' date.
 *
 * CONTRACT (hardened v1.0.09 — BUG: blank Scenario Planning page): an empty or
 * unparseable `iso` returns the INPUT UNCHANGED instead of throwing. The old
 * behaviour built `new Date(NaN)` and crashed on `.toISOString()` ("Invalid
 * time value"), which let a closed-popover sentinel (`weekStart: ''`) white-
 * screen the whole app. Callers that need to distinguish "invalid" should
 * validate before calling — this helper only guarantees it never throws.
 */
export function addDaysISO(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return iso;
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

export function addWeeksISO(iso: string, n: number): string {
  return addDaysISO(iso, n * 7);
}

/** Friday that ends the window of `weekCount` weeks starting at `saturday`. */
export function fridayAfter(saturday: string, weekCount: number): string {
  return addDaysISO(addWeeksISO(saturday, weekCount - 1), 6);
}

/** Saturdays spanning [startSat, endDate] inclusive. */
export function weekStartsInRange(startSat: string, endDate: string): string[] {
  const out: string[] = [];
  let cur = startSat;
  while (cur <= endDate) {
    out.push(cur);
    cur = addDaysISO(cur, 7);
  }
  return out;
}

/** Day offset 0..6 (Sat..Fri) of `iso` within the week beginning `weekStartSat`. */
export function dayIndexInWeek(iso: string, weekStartSat: string): number {
  const a = Date.parse(iso);
  const b = Date.parse(weekStartSat);
  return Math.round((a - b) / 86_400_000);
}

const DOW_LABELS = ['Sat', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
export function weekDayLabels(): string[] {
  return DOW_LABELS;
}

// ── week labelling (display convention) ─────────────────────────────────────
// The BST week's data key is always a Saturday, but schedulers think in
// Mon-start weeks — so EVERY user-facing week label is anchored to the MONDAY
// (Saturday + 2 days). Keep all week/day labelling going through these helpers
// so the surfaces never drift apart.

/** Monday (ISO) of the BST week whose Saturday start is `weekStartSat`. */
export function mondayOf(weekStartSat: string): string {
  return addDaysISO(weekStartSat, 2);
}

/** Short month-day label for the week's MONDAY, e.g. "Jun 22". */
export function weekOfMondayLabel(weekStartSat: string): string {
  const [y, m, d] = mondayOf(weekStartSat).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** Dated day headers for the Sat–Fri week: ["Sat 20", "Sun 21", "Mon 22", …]. */
export function datedDayLabels(weekStartSat: string): string[] {
  const [y, m, d] = weekStartSat.split('-').map(Number);
  return Array.from({ length: 7 }, (_, i) =>
    new Date(Date.UTC(y, m - 1, d + i)).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', timeZone: 'UTC' }),
  );
}

// ── working-day counting (binary person-day model, Wave 3) ───────────────────
// A working day is Mon–Fri minus supplied stat holidays (P-22, audit 2026-07-01:
// the backend numerator already skips stat holidays — passing the same holiday
// set keeps FE figures matching the backend header). No holidays supplied =
// legacy Mon–Fri counting. Under Wave 3 an assignment is PRESENCE: every
// working day its [start, end] covers is a busy day, and weekly figures read
// as busy days ÷ working days (Q5B — see utils/personDayDisplay.ts for the
// percent mapper; backend truth is backend/src/calculation/personDayModel.ts).

/**
 * True when `iso` parses as a real 'YYYY-MM-DD' date. Deterministic guard for
 * the working-day walkers below — an invalid date must return 0, not throw
 * (and must never enter the day-walk loop, where a non-advancing cursor would
 * spin forever). Hardened v1.0.09 (blank Scenario Planning page).
 */
export function isValidISODate(iso: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false;
  const [y, m, d] = iso.split('-').map(Number);
  return Number.isFinite(Date.UTC(y, m - 1, d));
}

/** Working days (Mon–Fri, minus supplied stat holidays) an assignment covers within its BST week. */
export function workingDaysCoveredInWeek(
  startISO: string,
  endISO: string,
  weekStartSat: string,
  holidays?: ReadonlySet<string>,
): number {
  // Invalid input ⇒ 0 covered days (never throw, never loop on a cursor that
  // can't advance). '' is the closed-popover sentinel; see isValidISODate.
  if (!isValidISODate(startISO) || !isValidISODate(endISO) || !isValidISODate(weekStartSat)) {
    return 0;
  }
  const weekFri = addDaysISO(weekStartSat, 6);
  const s = startISO < weekStartSat ? weekStartSat : startISO;
  const e = endISO > weekFri ? weekFri : endISO;
  if (!s || !e || s > e) return 0;
  let n = 0;
  let cur = s;
  while (cur <= e) {
    const [y, m, d] = cur.split('-').map(Number);
    const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0=Sun … 6=Sat
    if (dow !== 0 && dow !== 6 && !holidays?.has(cur)) n += 1;
    cur = addDaysISO(cur, 1);
  }
  return n;
}

/**
 * Working days (Mon–Fri minus stat holidays) in the BST week starting
 * `weekStartSat`. Invalid/empty input ⇒ 0 (see isValidISODate) — callers
 * already treat 0 working days as "nothing to show".
 */
export function workingDaysInWeek(weekStartSat: string, holidays?: ReadonlySet<string>): number {
  return workingDaysCoveredInWeek(weekStartSat, addDaysISO(weekStartSat, 6), weekStartSat, holidays);
}

/**
 * Wave 3 (Q5B) — DISTINCT busy working days covered by a set of bars within a
 * BST week (holiday-aware). Two tasks stacked on the same day count ONCE, so
 * the weekly total matches the backend's busyDays truth instead of a
 * double-counted per-row sum.
 */
export function distinctBusyWorkingDays(
  bars: ReadonlyArray<{ startDate: string; endDate: string }>,
  weekStartSat: string,
  holidays?: ReadonlySet<string>,
): number {
  const weekFri = addDaysISO(weekStartSat, 6);
  let n = 0;
  for (let cur = weekStartSat; cur <= weekFri; cur = addDaysISO(cur, 1)) {
    const [y, m, d] = cur.split('-').map(Number);
    const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0=Sun … 6=Sat
    if (dow === 0 || dow === 6 || holidays?.has(cur)) continue;
    if (bars.some((b) => b.startDate <= cur && b.endDate >= cur)) n += 1;
  }
  return n;
}

// ── assignment label (descriptive WO/project, not the test name) ─────────────
// Mirrors the Machine Schedule's bookingPrimaryLabel (utils/bookingLabel.ts) so
// the two schedules read a task the SAME way: the WORK ORDER's descriptive name
// with its number in brackets, falling back to the project name, then the test
// name. WorkOrderName is often blank (or a literal "null" in demo rows), so it's
// guarded. Absences (no WO/quote) keep their own name ("Vacation", etc.).

export interface AssignmentLabelFields {
  name: string;
  workOrderName?: string | null;
  projectName?: string | null;
  workOrderNo?: string | null;
  quoteNumber?: string | null;
}

/** Trimmed value unless it's blank or the literal string "null" (demo data). */
function meaningful(v: string | null | undefined): string | null {
  const t = v?.trim();
  return t && t.toLowerCase() !== 'null' ? t : null;
}

/** The descriptive bar label: WorkOrderName ?? ProjectName ?? test name (+ "(WO#)"). */
export function assignmentPrimaryLabel(a: AssignmentLabelFields): string {
  const wo = meaningful(a.workOrderNo);
  if (wo) {
    const desc = meaningful(a.workOrderName) ?? meaningful(a.projectName) ?? a.name;
    return desc ? `${desc} (${wo})` : wo;
  }
  const quote = meaningful(a.quoteNumber);
  if (quote) {
    const desc = meaningful(a.projectName) ?? a.name;
    return desc ? `${desc} (${quote})` : quote;
  }
  return meaningful(a.projectName) ?? a.name;
}

// ── tier grouping (Lead / Senior / Tech / Junior display groups) ─────────────
// Each Tier value gets its own Employee Schedule group header (RBAC audit D5 —
// Senior used to collapse into TEST TECHNICIANS, making the value cosmetic).
export function tierGroupLabel(tier: string | null): string {
  switch (tier) {
    case 'Lead': return 'GEOTECHNICAL LEADS';
    case 'Senior': return 'SENIOR TECHNICIANS';
    case 'Tech': return 'TEST TECHNICIANS';
    case 'Junior': return 'LAB STAFF';
    default: return 'UNASSIGNED';
  }
}
const GROUP_ORDER = ['GEOTECHNICAL LEADS', 'SENIOR TECHNICIANS', 'TEST TECHNICIANS', 'LAB STAFF', 'UNASSIGNED'];

export interface TierGroup {
  label: string;
  users: EmployeeBarUser[];
}

/** Bin users into tier groups, preserving server order within each group. */
export function buildTierGroups(users: EmployeeBarUser[]): TierGroup[] {
  const byLabel = new Map<string, EmployeeBarUser[]>();
  for (const u of users) {
    const label = tierGroupLabel(u.tier);
    const arr = byLabel.get(label) ?? [];
    arr.push(u);
    byLabel.set(label, arr);
  }
  return GROUP_ORDER.filter((l) => byLabel.has(l)).map((label) => ({ label, users: byLabel.get(label)! }));
}

// ── bar colour ──────────────────────────────────────────────────────────────
export function employeeBarColor(theme: Theme, kind: EmployeeBarKind): string {
  const s = theme.schedule;
  switch (kind) {
    case 'Vacation': return s.absenceVacation;
    case 'Sick': return s.absenceSick;
    case 'FieldWork': return s.absenceFieldWork;
    case 'Other': return s.absenceOther;
    default: return s.taskAssignment;
  }
}

/** Heatmap colour band for a capacity %, via theme tokens. */
export function capacityColor(theme: Theme, pct: number): string {
  const s = theme.schedule;
  if (pct <= 0) return s.noActivity;
  if (pct <= 70) return s.heatmapLow;
  if (pct <= 100) return s.heatmapMid;
  return s.heatmapHigh;
}

// ── lane assignment (stack overlapping bars so each is visible) ─────────────
export interface LanedBar { bar: EmployeeBar; lane: number; }

/**
 * Greedy interval-packing: each bar takes the first lane whose previous bar
 * ended strictly before this bar starts (inclusive dates → touching = overlap).
 * Concurrent assignments land in separate lanes so none hide behind another.
 */
export function assignLanes(bars: EmployeeBar[]): { laned: LanedBar[]; laneCount: number } {
  const sorted = [...bars].sort((a, b) => a.startDate.localeCompare(b.startDate) || a.endDate.localeCompare(b.endDate));
  const laneEnd: string[] = [];
  const laned: LanedBar[] = [];
  for (const bar of sorted) {
    let lane = laneEnd.findIndex((end) => end < bar.startDate);
    if (lane === -1) { lane = laneEnd.length; laneEnd.push(bar.endDate); }
    else laneEnd[lane] = bar.endDate;
    laned.push({ bar, lane });
  }
  return { laned, laneCount: Math.max(1, laneEnd.length) };
}
